import { useEffect } from 'react'
import { FormikProps } from 'formik'
import { useDispatch } from 'react-redux'
import { AddBadgesToEditInitialValues } from '@features/shift-post-v2/state/shift-post-v2.actions'
import { IShiftBadgesFormValues } from '@src/features/shift-post-v2/steps/screening-requirements-step/components/screening-section/screening-section.types'
import { IGigAutoAcceptCertificate } from 'models/GigAutoAcceptCertificate'
import { IAutoAcceptCertificate } from '@src/models/AutoAcceptCertificate'

type TUseShiftBadgesFormReturnType = {
  handleChange: (id: number) => void
  isCertificateSelected: (id: number) => boolean
}

/**
 * Hook that handles form logic for shift badges
 * @param formik - Formik instance
 * @param autoAcceptCertificates - Array of auto accept certificates
 * @returns {TUseShiftBadgesFormReturnType} Object containing form handlers
 */
export const useShiftBadgesForm = (
  formik: FormikProps<IShiftBadgesFormValues>,
  autoAcceptCertificates: (IGigAutoAcceptCertificate | IAutoAcceptCertificate)[]
): TUseShiftBadgesFormReturnType => {
  const dispatch = useDispatch()
  const { values, setFieldValue } = formik

  // Initialize badges in form values when certificates are loaded
  useEffect(() => {
    if (autoAcceptCertificates.length > 0 && !values.badges?.length) {
      const badgesWithSelection = autoAcceptCertificates.map((certificate) => ({
        ...certificate,
        selected: false,
      }))

      // Always set badges when certificates are available
      setFieldValue('badges', badgesWithSelection)

      // Set the initial values for the badges
      dispatch(AddBadgesToEditInitialValues(badgesWithSelection))
    }
  }, [autoAcceptCertificates, setFieldValue, dispatch, values.badges])

  // Update badges selection state based on auto_accept_certificate_ids
  useEffect(() => {
    if (values.badges && values.auto_accept_certificate_ids) {
      const updatedBadges = values.badges.map((badge) => ({
        ...badge,
        selected: values.auto_accept_certificate_ids.includes(badge.certificate.id),
      }))

      // Only update if the selection state actually changed
      const hasChanges = updatedBadges.some(
        (badge, index) => values.badges && badge.selected !== values.badges[index].selected
      )

      if (hasChanges) {
        setFieldValue('badges', updatedBadges)
      }
    }
  }, [values.auto_accept_certificate_ids, setFieldValue, values.badges])

  const handleChange = (id: number) => {
    const currentIds = values.auto_accept_certificate_ids || []
    const newIds = currentIds.includes(id)
      ? currentIds.filter((i) => i !== id)
      : [...currentIds, id]

    setFieldValue('auto_accept_certificate_ids', newIds)
  }

  const isCertificateSelected = (id: number) => {
    return (values.auto_accept_certificate_ids || []).includes(id)
  }

  return {
    handleChange,
    isCertificateSelected,
  }
}
